import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DataDialog } from "@app/components/atoms";
import { SignaturePad, type SignaturePadHandle } from "./SignaturePad";

export type SignatureDialogProps = {
  open: boolean;
  onClose: () => void;
  onSend: (signatureDataUrl: string | null) => void | Promise<void>;
  titleKey?: string;
  sendLabelKey?: string;
  isSubmitting?: boolean;
};

export function SignatureDialog({
  open,
  onClose,
  onSend,
  titleKey = "createInspection.signatureInput",
  sendLabelKey = "createInspection.send",
  isSubmitting = false,
}: SignatureDialogProps) {
  const { t } = useTranslation();
  const signaturePadRef = useRef<SignaturePadHandle>(null);
  const sendInFlightRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [showSignatureRequired, setShowSignatureRequired] = useState(false);

  useEffect(() => {
    if (!open) {
      sendInFlightRef.current = false;
      return;
    }
    setHasSignature(false);
    setShowSignatureRequired(false);
    const frameId = requestAnimationFrame(() => {
      signaturePadRef.current?.clear();
    });
    return () => cancelAnimationFrame(frameId);
  }, [open]);

  const handleClear = useCallback(() => {
    signaturePadRef.current?.clear();
    setShowSignatureRequired(false);
  }, []);

  const handleSend = useCallback(async () => {
    if (isSubmitting || sendInFlightRef.current) return;
    if (signaturePadRef.current?.isEmpty() ?? true) {
      setShowSignatureRequired(true);
      return;
    }
    setShowSignatureRequired(false);
    const signatureDataUrl = signaturePadRef.current?.toDataUrl() ?? null;
    sendInFlightRef.current = true;
    try {
      await onSend(signatureDataUrl);
    } finally {
      sendInFlightRef.current = false;
    }
  }, [isSubmitting, onSend]);

  return (
    <DataDialog
      open={open}
      onClose={onClose}
      title={t(titleKey)}
      fullWidth
      disableEnforceFocus
      className="dialog-container"
      dialogContentSx={{
        overflow: "visible",
        touchAction: "auto",
      }}
      dialogActions={
        <>
          <Button
            className="btn btn-no-border"
            style={{ width: "auto", marginRight: "0px" }}
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button
            className="btn btn-primary"
            style={{ width: "auto" }}
            onClick={handleSend}
            disabled={isSubmitting || !hasSignature}
          >
            {t("signature.sign")}
          </Button>
        </>
      }
    >
      <Box sx={{ py: 2, px: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("createInspection.signatureInputDescription")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: 560,
            mx: "auto",
          }}
        >
          <SignaturePad
            ref={signaturePadRef}
            onInkChange={(hasInk) => {
              setHasSignature(hasInk);
              if (hasInk) {
                setShowSignatureRequired(false);
              }
            }}
          />
          <Box
            component="button"
            type="button"
            onClick={handleClear}
            disabled={isSubmitting}
            sx={{
              border: "none",
              background: "none",
              padding: 0,
              mt: 1.5,
              alignSelf: "flex-start",
              fontSize: "1rem",
              fontWeight: 700,
              textDecoration: "none",
              textAlign: "left",
              color: "#140a9a",
              cursor: isSubmitting ? "default" : "pointer",
              opacity: isSubmitting ? 0.5 : 1,
            }}
          >
            {t("clear")}
          </Box>
          {showSignatureRequired && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", textAlign: "center", mt: 1 }}
            >
              {t("required")}
            </Typography>
          )}
        </Box>
      </Box>
    </DataDialog>
  );
}
